import {
  BillStatus,
  LegislativeType,
  SearchActionFilter,
} from '@enview/interface/types/Bill';
import { BillSearchParams, SortBy } from '@enview/interface/types/BillSearch';
import {
  ChamberType,
  LegislativeSessionSummary,
} from '@enview/interface/types/LegislativeSession';
import {
  InsightsPolicyTopic,
  InsightsPolicyTopicType,
} from '@enview/interface/types/bills/InsightsPolicyTopic';
import {
  ArrayParam,
  BooleanParam,
  DateParam,
  DecodedValueMap,
  DelimitedArrayParam,
  JsonParam,
  NumberParam,
  QueryParamConfigMap,
  StringParam,
  withDefault,
} from 'use-query-params';
import { CURRENT_SESSION_FILTER } from '../api/jurisdiction';
import { SortOrder } from '../models/SortOrder';
import { OptionType } from '../scenes/Shared/DropDowns/MultipleSelectDropDown';

const currentSessionDefault = {
  label: CURRENT_SESSION_FILTER,
  key: CURRENT_SESSION_FILTER,
};

// Helper function to get UTC date
const toUtcDate = (
  date: Date,
  hours: number,
  minutes: number,
  seconds: number,
  milliseconds: number,
) => {
  return new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      hours,
      minutes,
      seconds,
      milliseconds,
    ),
  ).toISOString();
};

export const TOO_MANY_RESULTS_THRESHOLD = 500;

export const defaultChamberSelection = [ChamberType.UPPER, ChamberType.LOWER];

export const chamberOptions: OptionType<string>[] = [
  { label: 'Upper Chamber', value: ChamberType.UPPER },
  { label: 'Lower Chamber', value: ChamberType.LOWER },
  { label: 'Both Chambers', value: ChamberType.BOTH },
];

type BillStatusOption = OptionType<string> & {
  applicableOnlyToJurisdictions?: string[];
};

// TODO: Move this logic to backend/interface so we don't have hardcoded values
// We should also update our jurisdictions table to include reference to country
export const statusOptions: BillStatusOption[] = [
  { label: 'Introduced', value: BillStatus.INTRODUCED },
  {
    label: 'First Reading',
    value: BillStatus.FIRST_READING,
    applicableOnlyToJurisdictions: ['ng', 'za'],
  },
  {
    label: 'Second Reading',
    value: BillStatus.SECOND_READING,
    applicableOnlyToJurisdictions: ['ng', 'il', 'za'],
  },
  {
    label: 'Referred to Committee',
    value: BillStatus.REFERRAL_COMMITTEE,
    applicableOnlyToJurisdictions: ['ng', 'il', 'za'],
  },
  {
    label: 'Reported out of Committee',
    value: BillStatus.REPORTED_OUT_OF_COMMITTEE,
    applicableOnlyToJurisdictions: ['ng', 'za'],
  },
  {
    label: 'Third Reading',
    value: BillStatus.THIRD_READING,
    applicableOnlyToJurisdictions: ['ng', 'il', 'za'],
  },
  { label: 'Passed Lower', value: BillStatus.PASSED_LOWER },
  { label: 'Passed Upper', value: BillStatus.PASSED_UPPER },
  { label: 'Passed', value: BillStatus.PASSED },
  {
    label: 'Sent for Concurrence',
    value: BillStatus.CONCURRENCE,
    applicableOnlyToJurisdictions: ['ng', 'za'],
  },
  {
    label: 'In Conference',
    value: BillStatus.NON_CONCURRENCE,
    applicableOnlyToJurisdictions: ['ma'],
  },
  { label: 'Signed by Governor', value: BillStatus.GOVERNOR },
  { label: 'Became Law', value: BillStatus.LAW },
  { label: 'Vetoed', value: BillStatus.VETOED },
  {
    label: 'Withdrawn',
    value: BillStatus.WITHDRAWAL,
    applicableOnlyToJurisdictions: ['ng', 'za'],
  },
];

export const BILL_STATUS_ORDER: BillStatus[] = statusOptions.map(
  (o) => o.value as BillStatus,
);

export const defaultStatusSelection = statusOptions.map(
  (statusOption) => statusOption.value,
);

// The "Latest Key Action" column only has meaningful data for Illinois bills,
// so it's only offered/rendered when every selected jurisdiction is Illinois.
export const isIllinoisOnlySearch = (jurisdictions?: string[]): boolean =>
  !!jurisdictions?.length &&
  jurisdictions.every((jurisdiction) => jurisdiction === 'il');

export const getStatusOptionsForJurisdictions = (
  selectedJurisdictions: string[],
): OptionType<string>[] => {
  return statusOptions.filter((statusOption) => {
    return (
      !statusOption.applicableOnlyToJurisdictions ||
      statusOption.applicableOnlyToJurisdictions.some((jurisdiction) =>
        selectedJurisdictions.includes(jurisdiction),
      )
    );
  });
};

export const defaultLegislativeTypeSelection = [
  LegislativeType.BILL,
  LegislativeType.RESOLUTION,
  LegislativeType.JOINT_RESOLUTION,
  LegislativeType.CONCURRENT_RESOLUTION,
  LegislativeType.PROCLAMATION,
  LegislativeType.PROPOSED_BILL,
  LegislativeType.APPROPRIATION,
  LegislativeType.APPOINTMENT,
  LegislativeType.OTHER,
];

export const legislativeTypeOptions: OptionType<string>[] = [
  { label: 'Bill', value: LegislativeType.BILL },
  { label: 'Resolution', value: LegislativeType.RESOLUTION },
  { label: 'Joint Resolution', value: LegislativeType.JOINT_RESOLUTION },
  { label: 'Concurrent Resolution', value: LegislativeType.CONCURRENT_RESOLUTION },
  { label: 'Proclamation', value: LegislativeType.PROCLAMATION },
  { label: 'Proposed Bill', value: LegislativeType.PROPOSED_BILL },
  { label: 'Appropriation', value: LegislativeType.APPROPRIATION },
  { label: 'Appointment', value: LegislativeType.APPOINTMENT },
  { label: 'Other', value: LegislativeType.OTHER },
];

export const getSessionOptions = (
  sessions: LegislativeSessionSummary[],
  includeJurisdictions?: boolean,
): OptionType<string>[] => {
  const currentSessionOption = {
    ...currentSessionDefault,
    value: CURRENT_SESSION_FILTER,
  };
  const sessionStrings: {
    key: string;
    value: string;
    jurisdictions: string[];
  }[] = [];
  sessions.forEach((session) => {
    if (!session.displayName) return;
    // Different jurisdictions often reuse the same display name (e.g. "2023-2024
    // Regular Session"), and the backend matches sessions by display name alone, so
    // these must collapse into a single option. Otherwise two options share the same
    // value and selecting one causes the other to render as checked too.
    const existingSession = sessionStrings.find(
      (sessionOption) => sessionOption.value === session.displayName,
    );
    if (existingSession) {
      if (!existingSession.jurisdictions.includes(session.jurisdiction)) {
        existingSession.jurisdictions.push(session.jurisdiction);
      }
      return;
    }
    sessionStrings.push({
      key: `${session.jurisdiction}-${session.displayName}`,
      // Annoyingly, display name needs to match DB values for search, so it's important that
      // session.displayName stay untouched as the value
      value: session.displayName,
      jurisdictions: [session.jurisdiction],
    });
  });
  const options = sessionStrings.map(({ key, value, jurisdictions }) => ({
    key,
    value,
    label: includeJurisdictions ? `${jurisdictions.join('/')} ${value}` : value,
  }));
  // Sort sessions in descending order (latest first)
  options.sort((a, b) => b.label.localeCompare(a.label));
  return [currentSessionOption, ...options];
};

export const getInsightsPolicyTopicOptions = (
  topics: InsightsPolicyTopic[] | undefined,
): OptionType<string>[] => {
  const topicsStrings: { label: string; key: string; value: string }[] = [];
  topics?.forEach((topic) => {
    const label = topic.policyTopic;
    if (label && label !== InsightsPolicyTopicType.UNTAGGABLE) {
      const key = `${topic.policyTopic}-${topic.id}`;
      const value = topic.insightName;
      topicsStrings.push({ key, label, value });
    }
  });
  return topicsStrings;
};

export const getSessionOptionsWithIds = (
  sessions: LegislativeSessionSummary[],
): OptionType<number>[] => {
  const currentSessionOption = {
    ...currentSessionDefault,
    value: -1,
  };
  const sessionStrings: { label: string; key: string; value: number }[] = [];
  sessions.forEach((session) => {
    const label = `${session.jurisdiction} ${session.displayName}`;
    const value = session.sessionId;
    const key = value.toString();
    const isLabelInSessions = sessionStrings.find(
      (sessionOption) => sessionOption.label === label,
    );
    if (!isLabelInSessions) {
      sessionStrings.push({ key, label, value });
    }
  });
  // Sort sessions in descending order (latest first)
  sessionStrings.sort((a, b) => b.label.localeCompare(a.label));
  return [currentSessionOption, ...sessionStrings].map(({ key, label, value }) => {
    return { label, value, key };
  });
};

export const getQueryParamConfig = (
  jurisdictionFilters: string[],
): QueryParamConfigMap => {
  return {
    keywords: withDefault(StringParam, undefined),
    page: withDefault(NumberParam, 1),
    pageSize: withDefault(NumberParam, undefined),
    order: withDefault(StringParam, null),
    status: withDefault(ArrayParam, defaultStatusSelection),
    sessions: withDefault(DelimitedArrayParam, [CURRENT_SESSION_FILTER]),
    jurisdictions: withDefault(DelimitedArrayParam, jurisdictionFilters),
    includeTrackedBills: withDefault(BooleanParam, true),
    chamber: withDefault(StringParam, ChamberType.BOTH),
    legislativeType: withDefault(ArrayParam, defaultLegislativeTypeSelection),
    searchActionType: withDefault(StringParam, undefined),
    searchActionFromDate: withDefault(DateParam, undefined),
    searchActionToDate: withDefault(DateParam, undefined),
    onlyShowMomentumBills: withDefault(BooleanParam, false),
    insightsPolicyTopic: withDefault(ArrayParam, undefined),
    tags: withDefault(ArrayParam, undefined),
    tagFilterMode: withDefault(StringParam, undefined),
    isAISearch: withDefault(BooleanParam, undefined),
    customFieldFilter: withDefault(JsonParam, undefined),
  };
};

export const buildParamsFromQuery = (
  query: DecodedValueMap<QueryParamConfigMap>,
  teamModeId: number,
  pageSize: number,
): BillSearchParams => {
  const chamberFilter =
    !query.chamber || query.chamber === ChamberType.BOTH ? undefined : [query.chamber];

  const searchActionFilter =
    query.searchActionType && (query.searchActionFromDate || query.searchActionToDate)
      ? ({
          type: query.searchActionType,
          fromDate: query.searchActionFromDate
            ? toUtcDate(query.searchActionFromDate, 0, 0, 0, 0)
            : undefined,
          toDate: query.searchActionToDate
            ? toUtcDate(query.searchActionToDate, 23, 59, 59, 999)
            : undefined,
        } as unknown as SearchActionFilter)
      : undefined;

  // Check for custom field sorting first
  const customFieldSort = query.order
    ? SortOrder.parseCustomFieldSortParam(query.order)
    : null;

  const order = query.order
    ? SortOrder.getBillSortOrderFromParam(query.order)
    : new SortOrder(query.keywords ? SortBy.RELEVANCE : SortBy.RECENT, false);
  return {
    criteria: {
      jurisdictions: query.jurisdictions as string[],
      sessionNames: query.sessions as string[],
      statuses: query.status,
      chambers: chamberFilter,
      actionFilter: searchActionFilter,
      legislativeTypes: query.legislativeType,
      includeTracked: query.includeTrackedBills,
      salienceScore: query.onlyShowMomentumBills ? 1 : undefined,
      query: query.keywords,
      teamId: teamModeId,
      useDefaultSession: !query.sessions || query.sessions.length === 0,
      insightsPolicyTopics: query.insightsPolicyTopic as string[],
      tagIds: query.tags as number[],
      tagFilterMode: query.tagFilterMode,
      trackingTeamsFilterMode: query.workspaceFilterMode,
      partySponsorFilters: query.sponsors as string[],
      isAISearch: query.isAISearch,
      customFieldFilter: query.customFieldFilter,
      newBillSourceSavedSearchIds: query.newBillSourceSavedSearchIds as string[],
      hasUnreadUpdates: query.hasUnreadUpdates || undefined,
    },
    pagination: {
      page: query.page,
      pageSize,
      sortOrder: order,
      customFieldSortTitle: customFieldSort?.fieldTitle,
      customFieldSortDesc: customFieldSort?.desc,
    },
  };
};

export const canonicalizeParams = (params: {
  jurisdictions: string[];
  query?: string;
  teamId?: number | null;
}): string => {
  const sortedJurisdictions = params.jurisdictions.slice().sort();

  const cleanQuery = params.query
    ? params.query.trim().replace(/\s+/g, ' ').toLowerCase()
    : null;

  const canonical = {
    jurisdictions: sortedJurisdictions,
    query: cleanQuery,
    teamId: params.teamId ?? null,
  };

  return JSON.stringify(canonical, Object.keys(canonical).sort());
};
